// Response schemas for user admin (plan §4 /admin/users, spec F18 and J9). Shapes follow
// apps/api/src/modules/users (schemas.ts UserView, OpenWork; service.ts DeactivateResult).
// Owner only, so nothing here is redacted today. The caps still carry REDACTION_HEADROOM, as in
// every other client schema (review B3-R1): one long field must never fail the whole page.
import { ROLES } from '@cis/shared';
import { z } from 'zod';

const REDACTION_HEADROOM = 6;
const EMAIL_MAX = 254;
const NAME_MAX = 120 * REDACTION_HEADROOM;
const SITE_MAX = 64 * REDACTION_HEADROOM;
// A task title is pack text (500 characters at most in the pack schema).
const TITLE_MAX = 500 * REDACTION_HEADROOM;
const TASK_ID_MAX = 64;
const STATUS_MAX = 40;
const INSTANT_MAX = 40;
const STEP_MAX = 10_000;

const id = z.number().int().positive();
const count = z.number().int().min(0);
const instant = z.string().max(INSTANT_MAX);

export const adminUserSchema = z.object({
  id,
  email: z.string().max(EMAIL_MAX),
  displayName: z.string().max(NAME_MAX),
  role: z.enum(ROLES),
  site: z.string().max(SITE_MAX).nullable(),
  active: z.boolean(),
  deactivatedAt: instant.nullable(),
  /** True once the person has signed in and their Entra object id is bound. */
  entraLinked: z.boolean(),
  version: id,
  createdAt: instant,
  updatedAt: instant,
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const adminUserPageSchema = z.object({
  items: z.array(adminUserSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: count,
});
export type AdminUserPage = z.infer<typeof adminUserPageSchema>;

export const openTaskSchema = z.object({
  taskId: z.string().max(TASK_ID_MAX),
  title: z.string().max(TITLE_MAX),
  status: z.string().max(STATUS_MAX),
});
export type OpenTask = z.infer<typeof openTaskSchema>;

export const openStepSchema = z.object({
  taskId: z.string().max(TASK_ID_MAX),
  stepNo: z.number().int().min(1).max(STEP_MAX),
});
export type OpenStep = z.infer<typeof openStepSchema>;

/** The work a deactivated person still holds: the reassignment list (spec J9). */
export const openWorkSchema = z.object({
  tasks: z.array(openTaskSchema),
  steps: z.array(openStepSchema),
});
export type OpenWork = z.infer<typeof openWorkSchema>;

export const deactivateResultSchema = z.object({
  user: adminUserSchema,
  sessionsRevoked: count,
  openWork: openWorkSchema,
});
export type DeactivateResult = z.infer<typeof deactivateResultSchema>;

export const revokeResultSchema = z.object({ sessionsRevoked: count });
export type RevokeResult = z.infer<typeof revokeResultSchema>;
